import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Platform,
  ScrollView,
  BackHandler,
  I18nManager,
} from 'react-native';
import * as Haptics from 'expo-haptics';
import { useRouter } from 'expo-router';
import {
  Accessibility,
  Plus,
  Minus,
  RotateCcw,
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Contrast,
  Palette,
  EyeOff,
  Link as LinkIcon,
  Heading,
  Keyboard,
  Ban,
  MousePointer2,
  ScanLine,
  FileText,
  X,
} from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { useAccessibility } from '@/contexts/AccessibilityContext';

const MENU_ID = 'accessibility-menu';
const MIN_TAP = 44;
const FAB_SIZE = 48;

export function AccessibilityButton() {
  const [showMenu, setShowMenu] = useState(false);
  const [fabFocused, setFabFocused] = useState(false);
  const [closeFocused, setCloseFocused] = useState(false);

  const router = useRouter();
  const floatingButtonRef = useRef<any>(null);
  const closeButtonRef = useRef<any>(null);

  const {
    settings,
    increaseFontSize,
    decreaseFontSize,
    setLetterSpacing,
    setLineHeight,
    setTextAlign,
    toggleReadableFont,
    setContrastMode,
    toggleGrayscale,
    toggleInvertColors,
    toggleHideImages,
    toggleHighlightLinks,
    toggleHighlightHeadings,
    toggleKeyboardNavigation,
    toggleStopAnimations,
    toggleBigCursor,
    toggleReadingGuide,
    resetAll,
  } = useAccessibility();

  const isWeb = Platform.OS === 'web';
  const isRTL = I18nManager.isRTL;

  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') return;

    if (showMenu) {
      setTimeout(() => {
        closeButtonRef.current?.focus?.();
      }, 100);
    }
  }, [showMenu, isWeb]);

  useEffect(() => {
    if (Platform.OS === 'android' && showMenu) {
      const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
        setShowMenu(false);
        return true;
      });
      return () => backHandler.remove();
    }
  }, [showMenu]);

  useEffect(() => {
    if (!isWeb || !showMenu || typeof window === 'undefined') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMenu(false);
        return;
      }

      if (e.key === 'Tab') {
        const menuElement = window.document.getElementById(MENU_ID);
        if (!menuElement) return;

        const focusableElements = menuElement.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey) {
          if (window.document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (window.document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target && target.closest && !target.closest(`#${MENU_ID}`)) {
        const menuElement = window.document.getElementById(MENU_ID);
        if (menuElement && !menuElement.contains(target)) {
          setShowMenu(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('click', handleClick, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('click', handleClick, true);
    };
  }, [isWeb, showMenu]);

  return (
    <>
      <Pressable
        ref={floatingButtonRef}
        style={({ pressed }) => [
          styles.floatingButton,
          pressed && styles.floatingButtonPressed,
          showMenu && styles.floatingButtonHidden,
          isWeb && fabFocused && styles.webFocusRing,
        ]}
        hitSlop={12}
        onPress={() => setShowMenu((prev) => !prev)}
        onFocus={() => isWeb && setFabFocused(true)}
        onBlur={() => isWeb && setFabFocused(false)}
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel="פתח הגדרות נגישות"
        accessibilityHint="לחץ כדי לפתוח תפריט הגדרות נגישות"
        accessibilityState={{ expanded: showMenu }}
        {...(isWeb && {
          tabIndex: 0,
          'aria-label': 'הגדרות נגישות',
          'aria-haspopup': 'dialog',
          'aria-controls': MENU_ID,
          'aria-expanded': showMenu,
        } as any)}
      >
        <Accessibility color="#FFFFFF" size={20} />
      </Pressable>

      {showMenu && (
        <View style={styles.backdrop}>
          {Platform.OS !== 'web' && (
            <Pressable
              style={styles.contentArea}
              onPress={() => {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                setShowMenu(false);
              }}
              accessible={false}
            />
          )}

          <Pressable
            {...(isWeb && { nativeID: MENU_ID } as any)}
            style={styles.menuSidebar}
            onPress={(e) => {
              if (Platform.OS === 'web') {
                e.stopPropagation();
              }
            }}
            accessible={true}
            accessibilityRole="menu"
            accessibilityLabel="הגדרות נגישות"
            accessibilityViewIsModal={true}
            {...(isWeb && {
              role: 'dialog',
              'aria-label': 'הגדרות נגישות',
              'aria-modal': 'true',
              dir: isRTL ? 'rtl' : 'ltr',
            } as any)}
          >
            {Platform.OS !== 'web' && (
              <View style={styles.dragHandleContainer}>
                <View style={styles.dragHandle} />
              </View>
            )}

            <View style={[styles.menuHeader, isRTL && styles.rowReverse]}>
              <View>
                <Text style={styles.menuTitle} accessibilityRole="header">
                  הגדרות נגישות
                </Text>
                <Text style={styles.menuSubtitle}>התאם את חוויית הצפייה שלך</Text>
              </View>

              <Pressable
                ref={closeButtonRef}
                onPress={() => {
                  if (Platform.OS !== 'web') {
                    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                  }
                  setShowMenu(false);
                }}
                onFocus={() => isWeb && setCloseFocused(true)}
                onBlur={() => isWeb && setCloseFocused(false)}
                hitSlop={12}
                style={({ pressed }) => [
                  styles.closeButton,
                  pressed && { opacity: 0.7, transform: [{ scale: 0.95 }] },
                  isWeb && closeFocused && styles.webFocusRing,
                ]}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel="סגור תפריט"
                accessibilityHint="לחץ או הקש Escape כדי לסגור"
                {...(isWeb && { tabIndex: 0 } as any)}
              >
                <X color="#6B7280" size={24} />
              </Pressable>
            </View>

            <ScrollView style={styles.menuContainer} contentContainerStyle={styles.menuContent}>
              <Pressable
                style={({ pressed }) => [
                  styles.resetButton,
                  pressed && styles.resetButtonPressed,
                ]}
                onPress={() => resetAll()}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel="איפוס הגדרות"
                hitSlop={8}
              >
                <View style={[styles.rowCenter, isRTL && styles.rowReverse]}>
                  <RotateCcw color={theme.colors.error} size={18} />
                  <Text style={styles.resetButtonText}>איפוס הגדרות</Text>
                </View>
              </Pressable>

              <View style={styles.divider} />

              <View style={styles.section}>
                <Text style={styles.sectionTitle} accessibilityRole="header">
                  התאמות טקסט
                </Text>

                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>גודל גופן ({settings.fontSize})</Text>
                  <View style={[styles.buttonGroup, isRTL && styles.rowReverse]}>
                    <Pressable
                      style={({ pressed }) => [
                        styles.iconButton,
                        pressed && styles.iconButtonPressed,
                        settings.fontSize >= 24 && styles.iconButtonDisabled,
                      ]}
                      onPress={increaseFontSize}
                      disabled={settings.fontSize >= 24}
                      accessible={true}
                      accessibilityRole="button"
                      accessibilityLabel="הגדל טקסט"
                      hitSlop={8}
                    >
                      <Plus
                        size={18}
                        color={settings.fontSize >= 24 ? theme.colors.textLight : theme.colors.primary}
                      />
                    </Pressable>

                    <Pressable
                      style={({ pressed }) => [
                        styles.iconButton,
                        pressed && styles.iconButtonPressed,
                        settings.fontSize <= 12 && styles.iconButtonDisabled,
                      ]}
                      onPress={decreaseFontSize}
                      disabled={settings.fontSize <= 12}
                      accessible={true}
                      accessibilityRole="button"
                      accessibilityLabel="הקטן טקסט"
                      hitSlop={8}
                    >
                      <Minus
                        size={18}
                        color={settings.fontSize <= 12 ? theme.colors.textLight : theme.colors.primary}
                      />
                    </Pressable>
                  </View>
                </View>

                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>ריווח אותיות</Text>
                  <View style={[styles.buttonGroup, isRTL && styles.rowReverse]}>
                    <Pressable
                      style={({ pressed }) => [
                        styles.optionButton,
                        pressed && styles.optionButtonPressed,
                        settings.letterSpacing === 'normal' && styles.optionButtonActive,
                      ]}
                      onPress={() => setLetterSpacing('normal')}
                      hitSlop={6}
                    >
                      <Text
                        style={[
                          styles.optionButtonText,
                          settings.letterSpacing === 'normal' && styles.optionButtonTextActive,
                        ]}
                      >
                        רגיל
                      </Text>
                    </Pressable>

                    <Pressable
                      style={({ pressed }) => [
                        styles.optionButton,
                        pressed && styles.optionButtonPressed,
                        settings.letterSpacing === 'wide' && styles.optionButtonActive,
                      ]}
                      onPress={() => setLetterSpacing('wide')}
                      hitSlop={6}
                    >
                      <Text
                        style={[
                          styles.optionButtonText,
                          settings.letterSpacing === 'wide' && styles.optionButtonTextActive,
                        ]}
                      >
                        רחב
                      </Text>
                    </Pressable>
                  </View>
                </View>

                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>גובה שורה</Text>
                  <View style={[styles.buttonGroup, isRTL && styles.rowReverse]}>
                    <Pressable
                      style={({ pressed }) => [
                        styles.optionButton,
                        pressed && styles.optionButtonPressed,
                        settings.lineHeight === 'normal' && styles.optionButtonActive,
                      ]}
                      onPress={() => setLineHeight('normal')}
                      hitSlop={6}
                    >
                      <Text
                        style={[
                          styles.optionButtonText,
                          settings.lineHeight === 'normal' && styles.optionButtonTextActive,
                        ]}
                      >
                        רגיל
                      </Text>
                    </Pressable>

                    <Pressable
                      style={({ pressed }) => [
                        styles.optionButton,
                        pressed && styles.optionButtonPressed,
                        settings.lineHeight === 'large' && styles.optionButtonActive,
                      ]}
                      onPress={() => setLineHeight('large')}
                      hitSlop={6}
                    >
                      <Text
                        style={[
                          styles.optionButtonText,
                          settings.lineHeight === 'large' && styles.optionButtonTextActive,
                        ]}
                      >
                        גדול
                      </Text>
                    </Pressable>
                  </View>
                </View>

                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>יישור טקסט</Text>
                  <View style={[styles.buttonGroup, isRTL && styles.rowReverse]}>
                    <Pressable
                      style={({ pressed }) => [
                        styles.iconButton,
                        pressed && styles.iconButtonPressed,
                        settings.textAlign === 'default' && styles.iconButtonActive,
                      ]}
                      onPress={() => setTextAlign('default')}
                      hitSlop={8}
                    >
                      <AlignJustify
                        size={16}
                        color={settings.textAlign === 'default' ? theme.colors.primary : theme.colors.text}
                      />
                    </Pressable>

                    <Pressable
                      style={({ pressed }) => [
                        styles.iconButton,
                        pressed && styles.iconButtonPressed,
                        settings.textAlign === 'right' && styles.iconButtonActive,
                      ]}
                      onPress={() => setTextAlign('right')}
                      hitSlop={8}
                    >
                      <AlignRight
                        size={16}
                        color={settings.textAlign === 'right' ? theme.colors.primary : theme.colors.text}
                      />
                    </Pressable>

                    <Pressable
                      style={({ pressed }) => [
                        styles.iconButton,
                        pressed && styles.iconButtonPressed,
                        settings.textAlign === 'center' && styles.iconButtonActive,
                      ]}
                      onPress={() => setTextAlign('center')}
                      hitSlop={8}
                    >
                      <AlignCenter
                        size={16}
                        color={settings.textAlign === 'center' ? theme.colors.primary : theme.colors.text}
                      />
                    </Pressable>

                    <Pressable
                      style={({ pressed }) => [
                        styles.iconButton,
                        pressed && styles.iconButtonPressed,
                        settings.textAlign === 'left' && styles.iconButtonActive,
                      ]}
                      onPress={() => setTextAlign('left')}
                      hitSlop={8}
                    >
                      <AlignLeft
                        size={16}
                        color={settings.textAlign === 'left' ? theme.colors.primary : theme.colors.text}
                      />
                    </Pressable>
                  </View>
                </View>

                <ToggleItem
                  rtl={isRTL}
                  icon={<Type size={20} color={settings.readableFont ? theme.colors.primary : theme.colors.text} />}
                  label="גופן קריא"
                  active={settings.readableFont}
                  onPress={toggleReadableFont}
                />
              </View>

              <View style={styles.divider} />

              <View style={styles.section}>
                <Text style={styles.sectionTitle} accessibilityRole="header">
                  התאמות ויזואליות
                </Text>

                <View style={styles.settingRow}>
                  <Text style={styles.settingLabel}>ניגודיות</Text>
                  <View style={[styles.buttonGroup, isRTL && styles.rowReverse]}>
                    <Pressable
                      style={({ pressed }) => [
                        styles.optionButton,
                        pressed && styles.optionButtonPressed,
                        settings.contrastMode === 'none' && styles.optionButtonActive,
                      ]}
                      onPress={() => setContrastMode('none')}
                      hitSlop={6}
                    >
                      <Text
                        style={[
                          styles.optionButtonText,
                          settings.contrastMode === 'none' && styles.optionButtonTextActive,
                        ]}
                      >
                        רגיל
                      </Text>
                    </Pressable>

                    <Pressable
                      style={({ pressed }) => [
                        styles.optionButton,
                        pressed && styles.optionButtonPressed,
                        settings.contrastMode === 'blackYellow' && styles.optionButtonActive,
                      ]}
                      onPress={() => setContrastMode('blackYellow')}
                      hitSlop={6}
                    >
                      <Text
                        style={[
                          styles.optionButtonText,
                          settings.contrastMode === 'blackYellow' && styles.optionButtonTextActive,
                        ]}
                      >
                        שחור/צהוב
                      </Text>
                    </Pressable>

                    <Pressable
                      style={({ pressed }) => [
                        styles.optionButton,
                        pressed && styles.optionButtonPressed,
                        settings.contrastMode === 'blackWhite' && styles.optionButtonActive,
                      ]}
                      onPress={() => setContrastMode('blackWhite')}
                      hitSlop={6}
                    >
                      <Text
                        style={[
                          styles.optionButtonText,
                          settings.contrastMode === 'blackWhite' && styles.optionButtonTextActive,
                        ]}
                      >
                        שחור/לבן
                      </Text>
                    </Pressable>
                  </View>
                </View>

                <ToggleItem
                  rtl={isRTL}
                  icon={<Palette size={20} color={settings.grayscale ? theme.colors.primary : theme.colors.text} />}
                  label="גווני אפור"
                  active={settings.grayscale}
                  onPress={toggleGrayscale}
                />

                <ToggleItem
                  rtl={isRTL}
                  icon={<Contrast size={20} color={settings.invertColors ? theme.colors.primary : theme.colors.text} />}
                  label="הפוך צבעים"
                  active={settings.invertColors}
                  onPress={toggleInvertColors}
                />

                <ToggleItem
                  rtl={isRTL}
                  icon={<EyeOff size={20} color={settings.hideImages ? theme.colors.primary : theme.colors.text} />}
                  label="הסתר תמונות"
                  active={settings.hideImages}
                  onPress={toggleHideImages}
                />

                <ToggleItem
                  rtl={isRTL}
                  icon={<LinkIcon size={20} color={settings.highlightLinks ? theme.colors.primary : theme.colors.text} />}
                  label="הדגש קישורים"
                  active={settings.highlightLinks}
                  onPress={toggleHighlightLinks}
                />

                <ToggleItem
                  rtl={isRTL}
                  icon={<Heading size={20} color={settings.highlightHeadings ? theme.colors.primary : theme.colors.text} />}
                  label="הדגש כותרות"
                  active={settings.highlightHeadings}
                  onPress={toggleHighlightHeadings}
                />
              </View>

              <View style={styles.divider} />

              <View style={styles.section}>
                <Text style={styles.sectionTitle} accessibilityRole="header">
                  אינטראקציה וניווט
                </Text>

                <ToggleItem
                  rtl={isRTL}
                  icon={<Keyboard size={20} color={settings.keyboardNavigation ? theme.colors.primary : theme.colors.text} />}
                  label="ניווט מקלדת"
                  active={settings.keyboardNavigation}
                  onPress={toggleKeyboardNavigation}
                />

                <ToggleItem
                  rtl={isRTL}
                  icon={<Ban size={20} color={settings.stopAnimations ? theme.colors.primary : theme.colors.text} />}
                  label="עצור אנימציות"
                  active={settings.stopAnimations}
                  onPress={toggleStopAnimations}
                />

                <ToggleItem
                  rtl={isRTL}
                  icon={<MousePointer2 size={20} color={settings.bigCursor ? theme.colors.primary : theme.colors.text} />}
                  label="סמן גדול"
                  active={settings.bigCursor}
                  onPress={toggleBigCursor}
                />

                <ToggleItem
                  rtl={isRTL}
                  icon={<ScanLine size={20} color={settings.readingGuide ? theme.colors.primary : theme.colors.text} />}
                  label="מדריך קריאה"
                  active={settings.readingGuide}
                  onPress={toggleReadingGuide}
                />
              </View>

              <View style={styles.divider} />

              <Pressable
                style={({ pressed }) => [
                  styles.statementButton,
                  pressed && styles.statementButtonPressed,
                  isRTL && styles.rowReverse,
                ]}
                onPress={() => {
                  router.push('/legal/accessibility');
                  setShowMenu(false);
                }}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel="הצהרת נגישות"
                hitSlop={8}
              >
                <FileText size={20} color={theme.colors.text} />
                <Text style={styles.statementButtonText}>הצהרת נגישות</Text>
              </Pressable>

              {Platform.OS !== 'web' && (
                <>
                  <View style={styles.divider} />
                  <Pressable
                    style={({ pressed }) => [
                      styles.closeMenuButton,
                      pressed && styles.closeMenuButtonPressed,
                      isRTL && styles.rowReverse,
                    ]}
                    onPress={() => {
                      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
                      setShowMenu(false);
                    }}
                    accessible={true}
                    accessibilityRole="button"
                    accessibilityLabel="סגור תפריט"
                    hitSlop={8}
                  >
                    <X color="#FFFFFF" size={20} />
                    <Text style={styles.closeMenuButtonText}>סגור</Text>
                  </Pressable>
                </>
              )}
            </ScrollView>
          </Pressable>
        </View>
      )}
    </>
  );
}

function ToggleItem({
  icon,
  label,
  active,
  onPress,
  rtl,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onPress: () => void;
  rtl: boolean;
}) {
  return (
    <Pressable
      style={({ pressed }) => [
        styles.toggleItem,
        pressed && styles.toggleItemPressed,
        active && styles.toggleItemActive,
        rtl && styles.rowReverse,
      ]}
      onPress={onPress}
      accessible={true}
      accessibilityRole="switch"
      accessibilityLabel={label}
      accessibilityState={{ checked: active }}
      hitSlop={8}
    >
      <View style={[styles.toggleContent, rtl && styles.rowReverse]}>
        {icon}
        <Text style={[styles.toggleLabel, active && styles.toggleLabelActive]}>{label}</Text>
      </View>
      <View style={[styles.toggleIndicator, active && styles.toggleIndicatorActive]} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  rowReverse: {
    flexDirection: 'row-reverse',
  },
  rowCenter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
  },

  floatingButton: {
    position: 'absolute',
    bottom: 20,
    left: 12,
    width: FAB_SIZE,
    height: FAB_SIZE,
    minWidth: MIN_TAP,
    minHeight: MIN_TAP,
    borderRadius: FAB_SIZE / 2,
    backgroundColor: theme.colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    ...theme.shadows.sm,
    zIndex: 1001,
    ...(Platform.OS === 'android' ? { elevation: 6 } : null),
  },
  floatingButtonPressed: {
    transform: [{ scale: 0.95 }],
    opacity: 0.9,
  },
  floatingButtonHidden: {
    zIndex: 999,
  },

  webFocusRing: {
    ...(Platform.OS === 'web'
      ? ({
          outlineStyle: 'solid',
          outlineWidth: 3,
          outlineColor: theme.colors.primary,
          outlineOffset: 2,
        } as any)
      : null),
  },

  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Platform.OS === 'web' ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
    zIndex: 1000,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    flexDirection: 'row',
  } as any,

  menuSidebar: {
    width: Platform.OS === 'web' ? 380 : '50%',
    maxWidth: Platform.OS === 'web' ? 380 : '50%',
    height: '100%',
    backgroundColor: '#F8F9FA',
    ...theme.shadows.lg,
    overflow: 'hidden',
  } as any,

  dragHandleContainer: {
    width: '100%',
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dragHandle: {
    width: 40,
    height: 4,
    backgroundColor: '#D1D5DB',
    borderRadius: 2,
  },

  menuHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    paddingBottom: theme.spacing.md,
    backgroundColor: '#F8F9FA',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },

  menuContainer: {
    flex: 1,
  },
  menuContent: {
    padding: theme.spacing.lg,
    paddingTop: theme.spacing.md,
    paddingBottom: Platform.OS === 'web' ? 40 : 60,
  },

  menuTitle: {
    fontSize: 24,
    fontFamily: theme.fonts.bold,
    color: '#1A1A1A',
    marginBottom: 4,
    textAlign: 'right',
  },
  menuSubtitle: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    textAlign: 'right',
  },

  closeButton: {
    padding: theme.spacing.xs,
    backgroundColor: 'transparent',
    borderRadius: theme.borderRadius.full,
    minWidth: MIN_TAP,
    minHeight: MIN_TAP,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: Platform.OS === 'web' ? 0 : 1,
    borderColor: Platform.OS === 'web' ? 'transparent' : '#E5E7EB',
  },

  resetButton: {
    backgroundColor: '#FFF',
    paddingVertical: 12,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    marginBottom: theme.spacing.md,
  },
  resetButtonPressed: {
    transform: [{ scale: 0.98 }],
    opacity: 0.85,
  },
  resetButtonText: {
    fontSize: 15,
    fontFamily: theme.fonts.semiBold,
    color: theme.colors.error,
  },

  divider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginVertical: theme.spacing.lg,
  },

  section: {
    gap: theme.spacing.sm,
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: '#1A1A1A',
    marginBottom: theme.spacing.sm,
    textAlign: 'right',
  },

  settingRow: {
    backgroundColor: '#FFF',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    gap: theme.spacing.sm,
  },
  settingLabel: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: '#1A1A1A',
    textAlign: 'right',
  },

  buttonGroup: {
    flexDirection: 'row',
    gap: theme.spacing.xs,
    justifyContent: 'flex-end',
  },

  iconButton: {
    width: MIN_TAP,
    height: MIN_TAP,
    borderRadius: theme.borderRadius.md,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  iconButtonPressed: {
    transform: [{ scale: 0.95 }],
    opacity: 0.75,
  },
  iconButtonDisabled: {
    opacity: 0.3,
  },
  iconButtonActive: {
    backgroundColor: theme.colors.primary + '20',
    borderColor: theme.colors.primary,
  },

  optionButton: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: theme.borderRadius.md,
    backgroundColor: '#F3F4F6',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    minHeight: MIN_TAP,
    justifyContent: 'center',
  },
  optionButtonPressed: {
    transform: [{ scale: 0.97 }],
    opacity: 0.8,
  },
  optionButtonActive: {
    backgroundColor: theme.colors.primary + '20',
    borderColor: theme.colors.primary,
  },
  optionButtonText: {
    fontSize: 13,
    fontFamily: theme.fonts.medium,
    color: '#1A1A1A',
    textAlign: 'center',
  },
  optionButtonTextActive: {
    color: theme.colors.primary,
    fontFamily: theme.fonts.semiBold,
  },

  toggleItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFF',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    minHeight: MIN_TAP,
  },
  toggleItemPressed: {
    transform: [{ scale: 0.99 }],
  },
  toggleItemActive: {
    borderColor: theme.colors.primary,
    backgroundColor: theme.colors.primary + '08',
  },
  toggleContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  toggleLabel: {
    fontSize: 15,
    fontFamily: theme.fonts.medium,
    color: '#1A1A1A',
    textAlign: 'right',
  },
  toggleLabelActive: {
    color: theme.colors.primary,
    fontFamily: theme.fonts.semiBold,
  },
  toggleIndicator: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFF',
  },
  toggleIndicatorActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },

  statementButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: '#FFF',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    minHeight: MIN_TAP,
  },
  statementButtonPressed: {
    transform: [{ scale: 0.99 }],
    opacity: 0.9,
  },
  statementButtonText: {
    fontSize: 15,
    fontFamily: theme.fonts.medium,
    color: '#1A1A1A',
    textAlign: 'right',
  },

  closeMenuButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.primary,
    paddingVertical: 14,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    marginTop: theme.spacing.md,
    minHeight: MIN_TAP,
    ...theme.shadows.sm,
  },
  closeMenuButtonPressed: {
    transform: [{ scale: 0.99 }],
    opacity: 0.92,
  },
  closeMenuButtonText: {
    fontSize: 16,
    fontFamily: theme.fonts.semiBold,
    color: '#FFFFFF',
  },

  contentArea: {
    flex: 1,
    backgroundColor: 'transparent',
  },
});
